(() => {
  const CARD_SELECTOR = '.product-item[data-json-product]';

  function handleize(value) {
    return String(value || '')
      .trim()
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ı/g, 'i')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function getProductData(card) {
    try {
      return JSON.parse(card.getAttribute('data-json-product'));
    } catch (error) {
      return null;
    }
  }

  function getVariantImageId(variant) {
    if (!variant) return null;

    if (variant.featured_media && variant.featured_media.id) {
      return String(variant.featured_media.id);
    }

    if (variant.featured_image && variant.featured_image.id) {
      return String(variant.featured_image.id);
    }

    return null;
  }

  function getImageMedia(data) {
    return (data.media || []).filter(media => {
      return !media.media_type || media.media_type === 'image';
    });
  }

  function findVariantForColor(card, data, variantId, colorValue) {
    const variants = data.variants || [];

    let variant = variants.find(item => {
      return String(item.id) === String(variantId);
    });

    const colorIndex = Number(
      card.dataset.baylorColorIndex || 0
    );

    if (colorValue) {
      const matchingVariant = variants.find(item => {
        const options = item.options || [];

        return (
          handleize(options[colorIndex]) === handleize(colorValue) &&
          getVariantImageId(item)
        );
      });

      if (matchingVariant) {
        variant = matchingVariant;
      }
    }

    return variant;
  }

  function getColorMedia(card, data, variantId, colorValue) {
    const media = getImageMedia(data);

    if (!media.length) {
      return [];
    }

    const variant = findVariantForColor(
      card,
      data,
      variantId,
      colorValue
    );

    const selectedImageId = getVariantImageId(variant);

    if (!selectedImageId) {
      return media;
    }

    const selectedIndex = media.findIndex(item => {
      return String(item.id) === selectedImageId;
    });

    if (selectedIndex === -1) {
      return media;
    }

    const colorStartIndexes = [];

    (data.variants || []).forEach(item => {
      const imageId = getVariantImageId(item);

      if (!imageId) return;

      const index = media.findIndex(mediaItem => {
        return String(mediaItem.id) === imageId;
      });

      if (
        index >= 0 &&
        !colorStartIndexes.includes(index)
      ) {
        colorStartIndexes.push(index);
      }
    });

    colorStartIndexes.sort((a, b) => a - b);

    const nextColorIndex = colorStartIndexes.find(index => {
      return index > selectedIndex;
    });

    const endIndex =
      typeof nextColorIndex === 'number'
        ? nextColorIndex
        : media.length;

    return media.slice(selectedIndex, endIndex);
  }

  function getMediaSrc(media) {
    if (
      media.preview_image &&
      media.preview_image.src
    ) {
      return media.preview_image.src;
    }

    return media.src || '';
  }

  function updateDots(gallery, activeIndex) {
    gallery
      .querySelectorAll('.baylor-card-gallery__dot')
      .forEach((dot, index) => {
        dot.classList.toggle(
          'is-active',
          index === activeIndex
        );
      });
  }

  function goToSlide(gallery, index, behavior = 'smooth') {
    const track = gallery.querySelector(
      '[data-baylor-card-track]'
    );

    if (!track) return;

    const slides = track.querySelectorAll(
      '.baylor-card-gallery__slide'
    );

    if (!slides.length) return;

    index = Math.max(
      0,
      Math.min(index, slides.length - 1)
    );

    track.scrollTo({
      left: track.clientWidth * index,
      behavior: behavior
    });

    gallery.dataset.currentSlide = String(index);

    updateDots(gallery, index);
  }

  function buildGallery(card, variantId, colorValue) {
    const data = getProductData(card);

    const gallery = card.querySelector(
      '[data-baylor-card-gallery]'
    );

    if (!data || !gallery) return;

    const track = gallery.querySelector(
      '[data-baylor-card-track]'
    );

    const dots = gallery.querySelector(
      '[data-baylor-gallery-dots]'
    );

    if (!track || !dots) return;

    const media = getColorMedia(
      card,
      data,
      variantId,
      colorValue
    );

    if (!media.length) {
      gallery.hidden = true;
      return;
    }

    track.innerHTML = '';
    dots.innerHTML = '';

    const productLink =
      card.querySelector('.card-link')?.href || '#';

    media.forEach((item, index) => {
      const src = getMediaSrc(item);

      if (!src) return;

      const slide = document.createElement('a');

      slide.className =
        'baylor-card-gallery__slide';

      slide.href = productLink;

      const image = document.createElement('img');

      image.src = src;
      image.alt = item.alt || '';
      image.draggable = false;

      if (index > 0) {
        image.loading = 'lazy';
      }

      slide.appendChild(image);
      track.appendChild(slide);

      const dot = document.createElement('button');

      dot.type = 'button';
      dot.className =
        'baylor-card-gallery__dot';

      dot.setAttribute(
        'aria-label',
        `${index + 1}. fotoğraf`
      );

      dot.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();

        goToSlide(gallery, index);
      });

      dots.appendChild(dot);
    });

    gallery.hidden = false;
    gallery.dataset.currentSlide = '0';

    track.scrollLeft = 0;

    updateDots(gallery, 0);
  }

  function initialiseCard(card) {
    if (card.dataset.baylorGalleryInit === '1') {
      return;
    }

    const gallery = card.querySelector(
      '[data-baylor-card-gallery]'
    );

    if (!gallery) return;

    card.dataset.baylorGalleryInit = '1';

    const track = gallery.querySelector(
      '[data-baylor-card-track]'
    );

    const firstSwatch =
      card.querySelector(
        '.swatch-label.is-active[data-variant-id]'
      ) ||
      card.querySelector(
        '.swatch-label[data-variant-id]'
      );

    const data = getProductData(card);

    let initialVariantId = null;
    let initialColor = null;

    if (firstSwatch) {
      initialVariantId = firstSwatch.dataset.variantId;
      initialColor = firstSwatch.dataset.value;
    } else if (
      data &&
      data.variants &&
      data.variants.length
    ) {
      initialVariantId = data.variants[0].id;
    }

    buildGallery(
      card,
      initialVariantId,
      initialColor
    );

    let scrollTicking = false;

    track.addEventListener('scroll', () => {
      if (scrollTicking) return;

      scrollTicking = true;

      requestAnimationFrame(() => {
        if (track.clientWidth) {
          const index = Math.round(
            track.scrollLeft / track.clientWidth
          );

          gallery.dataset.currentSlide =
            String(index);

          updateDots(gallery, index);
        }

        scrollTicking = false;
      });
    });

    const previousButton = gallery.querySelector(
      '[data-baylor-gallery-prev]'
    );

    const nextButton = gallery.querySelector(
      '[data-baylor-gallery-next]'
    );

    if (previousButton) {
      previousButton.addEventListener(
        'click',
        event => {
          event.preventDefault();
          event.stopPropagation();

          const current = Number(
            gallery.dataset.currentSlide || 0
          );

          goToSlide(gallery, current - 1);
        }
      );
    }

    if (nextButton) {
      nextButton.addEventListener(
        'click',
        event => {
          event.preventDefault();
          event.stopPropagation();

          const current = Number(
            gallery.dataset.currentSlide || 0
          );

          goToSlide(gallery, current + 1);
        }
      );
    }

    if (
      window.matchMedia('(hover: hover)').matches
    ) {
      gallery.addEventListener(
        'mouseenter',
        () => {
          const slides = track.querySelectorAll(
            '.baylor-card-gallery__slide'
          );

          if (slides.length > 1) {
            goToSlide(gallery, 1);
          }
        }
      );

      gallery.addEventListener(
        'mouseleave',
        () => {
          goToSlide(gallery, 0);
        }
      );
    }
  }

  function initialise(root = document) {
    if (
      root.matches &&
      root.matches(CARD_SELECTOR)
    ) {
      initialiseCard(root);
    }

    if (root.querySelectorAll) {
      root
        .querySelectorAll(CARD_SELECTOR)
        .forEach(initialiseCard);
    }
  }

  document.addEventListener('click', event => {
    const swatch = event.target.closest(
      '.swatch-label[data-variant-id]'
    );

    if (!swatch) return;

    const card = swatch.closest('.product-item');

    if (!card) return;

    setTimeout(() => {
      card
        .querySelectorAll('.swatch-label')
        .forEach(label => {
          label.classList.remove('is-active');
        });

      swatch.classList.add('is-active');

      buildGallery(
        card,
        swatch.dataset.variantId,
        swatch.dataset.value
      );
    }, 30);
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initialise(document);
      }
    );
  } else {
    initialise(document);
  }

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1) {
          initialise(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();